import { CROWD_EFFECTS, type CrowdEffectDef } from '../content/crowd';
import type { World } from './world';
import type { PlayerEnt } from './types';
import { spawnMob } from './mobs';
import { applyStatus } from './status';
import { heal } from './combat';
import { addConsumable } from './player';
import { rollItem, spawnPickup } from './loot';
import { addCorruption, spawnBoss, spawnRevenantNear } from './realm';

export interface CrowdRefusal {
  err: string;
}

/**
 * Applies a show-mode effect bought by a spectator to the watched hero.
 * Returns an error key when the realm state forbids it (already in the boss phase, no ghosts, ...).
 */
export function applyCrowdEffect(world: World, target: PlayerEnt, def: CrowdEffectDef, by: string): CrowdRefusal | null {
  const a = def.apply;
  switch (a.t) {
    case 'spawn': {
      for (let i = 0; i < a.count; i++) {
        const ang = world.rng.angle();
        const d = world.rng.float(260, 380);
        const pos = world.map.randomPointNear(world.rng, target.x, target.y, 240, 400, 16) ?? { x: target.x + Math.cos(ang) * d, y: target.y + Math.sin(ang) * d };
        const m = spawnMob(world, a.unit, pos.x, pos.y, { elite: a.elite, name: by, level: Math.max(1, target.level) });
        // the crowd's creatures scale with the hero so late realms stay honest
        const mul = 1 + Math.max(0, target.level - 1) * 0.08;
        m.maxHp = Math.round(m.maxHp * mul);
        m.hp = m.maxHp;
        m.scaleDamage *= 0.8 + target.level * 0.04;
        m.xpValue = Math.round(m.xpValue * 0.5);
        m.target = target.id;
      }
      break;
    }
    case 'status':
      applyStatus(world, target, { id: a.id as never, duration: a.duration, stacks: a.stacks }, null, target.power);
      break;
    case 'boss': {
      const st = world.realm;
      if (!st) return { err: 'crowd.notRealm' };
      if (st.phase !== 'open') return { err: 'crowd.bossAlready' };
      spawnBoss(world);
      break;
    }
    case 'corruption': {
      const st = world.realm;
      if (!st || st.phase !== 'open') return { err: 'crowd.bossAlready' };
      addCorruption(world, (st.corruptionNeeded * a.add) / 100);
      break;
    }
    case 'revenant': {
      const st = world.realm;
      if (!st) return { err: 'crowd.notRealm' };
      const ghosts = st.ghosts.filter((g) => g.accountId !== target.accountId);
      if (!ghosts.length) return { err: 'crowd.noGhosts' };
      const g = ghosts[world.rng.int(0, ghosts.length - 1)];
      const rev = spawnRevenantNear(world, g, target);
      if (!rev) return { err: 'crowd.noRoom' };
      rev.target = target.id;
      break;
    }
    case 'heal':
      heal(world, target, target.maxHp * a.pct, null);
      break;
    case 'shield':
      applyStatus(world, target, { id: 'shield', duration: a.duration, power: a.power }, null, target.power);
      break;
    case 'gift':
      if (!addConsumable(target, a.consumable)) return { err: 'crowd.bagFull' };
      world.events.push({ t: 'pickup', id: target.id, type: 'consumable', what: a.consumable });
      break;
    case 'loot': {
      const item = rollItem(world.rng, Math.max(1, Math.min(3, world.map.tierAt(target.x, target.y))), target.stats.luck + 1, world.allocId(), 1);
      spawnPickup(world, target.x + 30, target.y, { type: 'item', item, owner: target.id, ttl: 90 });
      break;
    }
  }
  if (def.kind === 'harm') target.crowdFedAt = world.tick;
  world.events.push({ t: 'crowd', id: target.id, effect: def.id, by, kind: def.kind, x: target.x, y: target.y });
  world.events.push({ t: 'feed', key: def.kind === 'harm' ? 'feed.crowdHarm' : 'feed.crowdHelp', vars: { by, target: target.name, effect: def.id } });
  return null;
}

export function crowdEffect(id: string): CrowdEffectDef | undefined {
  return CROWD_EFFECTS[id];
}
